import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getSvgMarker } from '../helpers/utils';
import {
  BaseMapProvider,
  MarkerOptions,
  RouteRequest,
  RouteResult,
} from './maps/map-provider.service';
import { RouteConsistencyService } from './maps/route-consistency.service';
import { osService } from './osService.service';

declare var mapkit: any;

export interface MapKitCoordinate {
  latitude: number;
  longitude: number;
}

export interface MapKitCoordinateSpan {
  latitudeDelta: number;
  longitudeDelta: number;
}

export interface MapKitCoordinateRegion {
  center: MapKitCoordinate;
  span: MapKitCoordinateSpan;
}

export interface MapKitGeocoderResult {
  formattedAddress: string;
  coordinate: MapKitCoordinate;
  countryCode?: string;
  administrativeArea?: string;
  locality?: string;
  subLocality?: string;
  thoroughfare?: string;
  subThoroughfare?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MapKitService extends BaseMapProvider {
  private geocoder: any = null;
  private directions: any = null;
  private isInitialized = false;
  private initPromise?: Promise<void>;
  private scriptPromise?: Promise<void>;
  private readonly routeTimeoutMs = 6000;

  constructor(
    private http: HttpClient,
    private osService: osService,
    private routeConsistencyService: RouteConsistencyService
  ) {
    super();
  }

  async initialize(): Promise<void> {
    await this.ensureInitialized();
  }

  override async initializeMap(
    element: HTMLElement,
    options: any = {}
  ): Promise<any> {
    await this.ensureInitialized();
    const mapOptions = this.buildMapOptions(options);
    this.map = new mapkit.Map(element, mapOptions);

    if (options.center) {
      this.setMapCenter(this.map, options.center);
    }

    return this.map;
  }

  override createMarker(position: any, options: MarkerOptions = {}): any {
    if (!position) {
      return null;
    }

    const coordinate = this.createCoordinate(
      position.lat || position.latitude,
      position.lng || position.longitude
    );

    let marker: any;
    if (options.icon) {
      const resolvedIcon = this.resolveIconPath(options.icon);
      const width = options.iconSize?.width || 40;
      const height = options.iconSize?.height || 40;

      marker = new mapkit.ImageAnnotation(coordinate, {
        url: { 1: resolvedIcon, 2: resolvedIcon, 3: resolvedIcon },
        size: {
          width: width,
          height: height,
        },
        // Ne pas spécifier anchorOffset - utiliser l'ancre par défaut de MapKit
      });
    } else {
      marker = new mapkit.MarkerAnnotation(coordinate, {
        title: options.title ?? '',
        color: '#1f94a4',
      });
    }

    // Ajouter à la carte seulement si autoAdd n'est pas false (option non typée pour compatibilité)
    const autoAdd = (options as any).autoAdd !== false;
    if (autoAdd && this.map) {
      this.map.addAnnotation(marker);
      this.addMarker(marker);
    }
    return marker;
  }

  override removeMarker(marker: any): void {
    if (!marker) {
      return;
    }

    // Toujours retirer de la liste interne
    this.removeMarkerFromList(marker);

    if (!this.map) {
      return;
    }

    try {
      // Vérifier si l'annotation est réellement attachée à la carte avant de la supprimer
      const annotations = this.map.annotations;
      if (!annotations) {
        // Pas d'annotations sur la carte, rien à faire
        return;
      }

      // MapKit annotations peut être un objet avec une méthode length ou un tableau
      let isAttached = false;

      if (Array.isArray(annotations)) {
        isAttached = annotations.includes(marker);
      } else if (annotations.length !== undefined) {
        // Si c'est un objet array-like, vérifier chaque élément
        for (let i = 0; i < annotations.length; i++) {
          if (annotations[i] === marker) {
            isAttached = true;
            break;
          }
        }
      }

      // Ne supprimer que si l'annotation est réellement attachée
      if (isAttached) {
        this.map.removeAnnotation(marker);
      }
      // Si pas attachée, on ne fait rien (déjà retirée de la liste)
    } catch (error: any) {
      // Si l'erreur indique que l'annotation n'est pas attachée, ignorer silencieusement
      const errorMessage = error?.message || String(error);
      if (errorMessage.includes('not attached') || errorMessage.includes('cannot remove annotation')) {
        return;
      }
      console.debug('Error removing MapKit marker:', error);
    }
  }

  override clearMarkers(markers: any[]): void {
    markers.forEach((marker) => this.removeMarker(marker));
  }

  override updateMarkerPosition(marker: any, position: any): void {
    if (marker && position) {
      marker.coordinate = this.createCoordinate(
        position.lat || position.latitude,
        position.lng || position.longitude
      );
    }
  }

  override setMapCenter(map: any, center: any): void {
    if (map && center) {
      const coordinate = this.createCoordinate(
        center.lat || center.latitude,
        center.lng || center.longitude
      );
      map.setCenterAnimated(coordinate);
    }
  }

  override destroyMap(map: any): void {
    this.clearAllMarkers();
    if (map && typeof map.destroy === 'function') {
      map.destroy();
    }
    this.map = null;
  }

  override async calculateRoute(
    request: RouteRequest
  ): Promise<RouteResult> {
    await this.ensureInitialized();

    return new Promise<RouteResult>((resolve, reject) => {
      const normalized = this.routeConsistencyService.normalizeRouteRequest(
        request
      );
      if (!this.routeConsistencyService.validateRouteRequest(normalized)) {
        reject(new Error('Invalid route request'));
        return;
      }

      const originCoords =
        this.routeConsistencyService.normalizeCoordinates(normalized.origin);
      const destinationCoords =
        this.routeConsistencyService.normalizeCoordinates(
          normalized.destination
        );

      const origin = this.createCoordinate(originCoords.lat, originCoords.lng);
      const destination = this.createCoordinate(
        destinationCoords.lat,
        destinationCoords.lng
      );

      let settled = false;
      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        reject(new Error(`MapKit route timeout after ${this.routeTimeoutMs}ms`));
      }, this.routeTimeoutMs);

      this.directions.route(
        {
          origin,
          destination,
          transportType: mapkit.Directions.Transport.Automobile,
        },
        (error: any, data: any) => {
          if (settled) {
            return;
          }
          settled = true;
          window.clearTimeout(timeoutId);

          if (error) {
            console.warn("[MapKit][route] Directions error", {
              origin: originCoords,
              destination: destinationCoords,
              error: error?.message ?? String(error),
            });
            reject(error);
            return;
          }

          const route = data?.routes?.[0];
          if (!route) {
            console.warn("[MapKit][route] No route returned", {
              origin: originCoords,
              destination: destinationCoords,
              routesCount: data?.routes?.length ?? 0,
              hasData: !!data,
            });
            reject(new Error('No route returned by MapKit'));
            return;
          }

          // Vérifier que la route a des données valides
          if (!route.distance && !route.expectedTravelTime) {
            reject(new Error('MapKit route is invalid: missing distance and travel time'));
            return;
          }

          const totalSeconds = route.expectedTravelTime || 0;
          const routeResult: RouteResult = {
            distance: this.osService.standardizeDistance(route.distance || 0),
            duration: {
              hours: Math.floor(totalSeconds / 3600),
              minutes: Math.floor((totalSeconds % 3600) / 60),
            },
            route: data,
          };

          this.routeConsistencyService.validateRouteResult(
            routeResult,
            normalized.origin,
            normalized.destination
          );
          this.routeConsistencyService.logRouteResult(routeResult, 'MapKit');
          resolve(routeResult);
        }
      );
    });
  }

  resetInitialisation(): void {
    this.isInitialized = false;
    this.map = null;
    this.geocoder = null;
    this.directions = null;
  }

  refreshMap(mapInstance: any = this.map): void {
    if (!mapInstance) {
      return;
    }

    try {
      const annotations = mapInstance.annotations || [];
      const overlays = mapInstance.overlays || [];
      annotations.forEach((annotation: any) =>
        mapInstance.removeAnnotation(annotation)
      );
      if (overlays.length) {
        mapInstance.removeOverlays(overlays);
      }
    } catch (error) {
      console.error(error);
    }
  }

  containerExist(containerId: string): boolean {
    return !!document.getElementById(containerId);
  }

  setMapBounds(map: any, latitude: number, longitude: number): void {
    this.setMapCenter(map, { latitude, longitude });
  }

  createCoordinate(latitude: number, longitude: number): any {
    return new mapkit.Coordinate(latitude, longitude);
  }

  createCoordinateSpan(latitudeDelta: number, longitudeDelta: number): any {
    return new mapkit.CoordinateSpan(latitudeDelta, longitudeDelta);
  }

  createCoordinateRegion(
    center: MapKitCoordinate,
    span: MapKitCoordinateSpan
  ): any {
    const mapKitCenter = this.createCoordinate(
      center.latitude,
      center.longitude
    );
    const mapKitSpan = this.createCoordinateSpan(
      span.latitudeDelta,
      span.longitudeDelta
    );
    return new mapkit.CoordinateRegion(mapKitCenter, mapKitSpan);
  }

  setRegion(
    map: any,
    region: { center: MapKitCoordinate; span: MapKitCoordinateSpan }
  ): void {
    if (!map || !region?.center || !region?.span) {
      return;
    }
    map.region = this.createCoordinateRegion(region.center, region.span);
  }

  getCenter(map: any): MapKitCoordinate | null {
    if (map?.center) {
      return {
        latitude: map.center.latitude,
        longitude: map.center.longitude,
      };
    }
    return null;
  }

  getRegion(map: any): MapKitCoordinateRegion | null {
    if (map?.region) {
      return {
        center: {
          latitude: map.region.center.latitude,
          longitude: map.region.center.longitude,
        },
        span: {
          latitudeDelta: map.region.span.latitudeDelta,
          longitudeDelta: map.region.span.longitudeDelta,
        },
      };
    }
    return null;
  }

  async geocodeAddress(address: string): Promise<MapKitGeocoderResult[]> {
    await this.ensureInitialized();
    if (!this.geocoder) {
      throw new Error('Géocodeur MapKit non initialisé');
    }

    return new Promise((resolve, reject) => {
      this.geocoder.lookup(address, (error: any, data: any) => {
        if (error) {
          reject(error);
          return;
        }

        if (data?.results) {
          resolve(
            data.results.map((result: any) => ({
              formattedAddress: result.formattedAddress,
              coordinate: {
                latitude: result.coordinate.latitude,
                longitude: result.coordinate.longitude,
              },
              countryCode: result.countryCode,
              administrativeArea: result.administrativeArea,
              locality: result.locality,
              subLocality: result.subLocality,
              thoroughfare: result.thoroughfare,
              subThoroughfare: result.subThoroughfare,
            }))
          );
        } else {
          resolve([]);
        }
      });
    });
  }

  async reverseGeocode(
    coordinate: MapKitCoordinate
  ): Promise<MapKitGeocoderResult[]> {
    await this.ensureInitialized();
    if (!this.geocoder) {
      throw new Error('Géocodeur MapKit non initialisé');
    }

    const mapKitCoordinate = this.createCoordinate(
      coordinate.latitude,
      coordinate.longitude
    );

    return new Promise((resolve, reject) => {
      this.geocoder.reverseLookup(mapKitCoordinate, (error: any, data: any) => {
        if (error) {
          reject(error);
          return;
        }

        if (data?.results) {
          resolve(
            data.results.map((result: any) => ({
              formattedAddress: result.formattedAddress,
              coordinate: {
                latitude: result.coordinate.latitude,
                longitude: result.coordinate.longitude,
              },
              countryCode: result.countryCode,
              administrativeArea: result.administrativeArea,
              locality: result.locality,
              subLocality: result.subLocality,
              thoroughfare: result.thoroughfare,
              subThoroughfare: result.subThoroughfare,
            }))
          );
        } else {
          resolve([]);
        }
      });
    });
  }

  isMapKitInitialized(): boolean {
    return this.isInitialized;
  }

  getGeocoder(): any {
    return this.geocoder;
  }

  drawRoute(path: any[], mapInstance: any = this.map): any {
    if (!path?.length || !mapInstance) {
      console.warn('drawRoute: invalid parameters', {
        pathLength: path?.length,
        hasMapInstance: !!mapInstance
      });
      return null;
    }

    try {
      // Normaliser les coordonnées pour s'assurer qu'elles sont valides
      const coords = path
        .map((point: any) => {
          const lat = point.lat || point.latitude;
          const lng = point.lng || point.longitude;
          if (lat && lng && typeof lat === 'number' && typeof lng === 'number') {
            return this.createCoordinate(lat, lng);
          }
          return null;
        })
        .filter((coord: any) => coord !== null);

      if (!coords.length) {
        console.warn('drawRoute: no valid coordinates after normalization', path);
        return null;
      }

      // Créer un style plus doux et subtil pour la route
      // IMPORTANT: strokeOpacity doit être défini pour que la ligne soit visible
      const routeStyle = new mapkit.Style({
        lineWidth: 4, // Ligne fine mais visible
        strokeColor: '#4088CD', // Bleu standard pour la cohérence avec Google Maps
        strokeOpacity: 1.0, // Opacité complète pour s'assurer que la ligne est visible
        lineJoin: 'round',
        lineCap: 'round',
      });

      // Créer l'overlay et appliquer le style
      const overlay = new mapkit.PolylineOverlay(coords);
      overlay.style = routeStyle;

      // Vérifier que le style est bien appliqué
      if (!overlay.style) {
        console.error('drawRoute: overlay has no style after assignment');
        try {
          overlay.style = new mapkit.Style({
            lineWidth: 4,
            strokeColor: '#4088CD',
            strokeOpacity: 1.0,
            lineJoin: 'round',
            lineCap: 'round',
          });
        } catch (e) {
          console.error('drawRoute: failed to reapply style', e);
          return null;
        }
      }

      if (!mapInstance || typeof mapInstance.addOverlay !== 'function') {
        console.error('drawRoute: mapInstance is invalid', {
          hasMapInstance: !!mapInstance,
          hasAddOverlay: typeof mapInstance?.addOverlay === 'function',
          mapInstanceType: mapInstance?.constructor?.name
        });
        return null;
      }

      try {
        // Ajouter l'overlay à la carte
        mapInstance.addOverlay(overlay);

        // S'assurer que le style est correctement appliqué
        if (overlay.style) {
          if (overlay.style.strokeOpacity === undefined || overlay.style.strokeOpacity === 0) {
            overlay.style.strokeOpacity = 1.0;
          }
          if (!overlay.style.lineWidth || overlay.style.lineWidth < 2) {
            overlay.style.lineWidth = 4;
          }
        }

        // S'assurer que l'overlay est visible
        if (overlay.visible === false) {
          overlay.visible = true;
        }

        // Forcer un refresh après un court délai
        setTimeout(() => {
          try {
            if (mapInstance.overlays && mapInstance.overlays.includes(overlay)) {
              if (overlay.style && overlay.style.strokeOpacity !== 1.0) {
                overlay.style.strokeOpacity = 1.0;
              }
              if (typeof mapInstance.update === 'function') {
                mapInstance.update();
              }
            }
          } catch (refreshError) {
            console.warn('drawRoute: error refreshing overlay', refreshError);
          }
        }, 100);

        return overlay;
      } catch (addError) {
        console.error('drawRoute: error adding overlay to map', addError);
        return null;
      }
    } catch (error) {
      console.error('drawRoute: error creating overlay', error);
      return null;
    }
  }

  getDirections(
    origin: any,
    destination: any,
    callback: (error: any, data: any) => void
  ): any {
    if (!this.directions) {
      throw new Error('Directions service not initialized');
    }

    return this.directions.route(
      {
        origin,
        destination,
        transportType: mapkit.Directions.Transport.Automobile,
      },
      (error: any, data: any) => {
        callback(error, data);
      }
    );
  }

  createImageMarker(
    lat: number,
    lng: number,
    icon: string,
    offset: DOMPoint | null = null,
    width = 40,
    height = 40
  ) {
    if (!lat || !lng) {
      return null;
    }
    return new mapkit.ImageAnnotation(this.createCoordinate(lat, lng), {
      url: { 1: icon, 2: icon, 3: icon },
      size: { width, height },
      anchorOffset: offset ?? new DOMPoint(0, 0),
    });
  }

  addEventListener(
    mapInstance: any,
    eventType: string,
    callback: (event: any) => void
  ): void {
    if (mapInstance) {
      mapInstance.addEventListener(eventType, (event: any) => {
        const normalizedEvent: any = { type: eventType };

        if (event.coordinate) {
          normalizedEvent.coordinate = {
            latitude: event.coordinate.latitude,
            longitude: event.coordinate.longitude,
          };
        }

        if (event.region) {
          normalizedEvent.region = {
            center: {
              latitude: event.region.center.latitude,
              longitude: event.region.center.longitude,
            },
            span: {
              latitudeDelta: event.region.span.latitudeDelta,
              longitudeDelta: event.region.span.longitudeDelta,
            },
          };
        }

        if (event.pointOnPage && this.map) {
          const point = this.map.convertPointOnPageToCoordinate(event.pointOnPage);
          normalizedEvent.coordinate = {
            latitude: point.latitude,
            longitude: point.longitude,
          };
        }

        callback(normalizedEvent);
      });
    }
  }

  removeEventListener(
    mapInstance: any,
    eventType: string,
    callback: (event: any) => void
  ): void {
    if (mapInstance) {
      mapInstance.removeEventListener(eventType, callback);
    }
  }

  private buildMapOptions(options: any): any {
    // Déterminer si le compass doit être affiché
    const showsCompass = options?.showsCompass !== undefined
      ? (typeof options.showsCompass === 'boolean'
        ? (options.showsCompass ? mapkit.FeatureVisibility.Adaptive : mapkit.FeatureVisibility.Hidden)
        : options.showsCompass)
      : mapkit.FeatureVisibility.Adaptive;

    // Le compass nécessite que la rotation soit activée
    // Si showsCompass n'est pas Hidden, activer automatiquement la rotation
    const isCompassVisible = showsCompass !== mapkit.FeatureVisibility.Hidden;
    const isRotationEnabled = options?.isRotationEnabled ?? isCompassVisible;

    const mapOptions: any = {
      showsCompass: showsCompass,
      showsZoomControl: options?.showsZoomControl !== undefined
        ? (typeof options.showsZoomControl === 'boolean'
          ? (options.showsZoomControl ? mapkit.FeatureVisibility.Adaptive : mapkit.FeatureVisibility.Hidden)
          : options.showsZoomControl)
        : mapkit.FeatureVisibility.Adaptive,
      showsMapTypeControl: false,
      isRotationEnabled: isRotationEnabled,
      showsUserLocation: options?.showsUserLocation ?? false,
      showsUserLocationControl: options?.showsUserLocationControl ?? false,
      showsPointsOfInterest: true,
      loadPriority: 'PointsOfInterest',
    };

    if (options?.region) {
      mapOptions.region = this.createCoordinateRegion(
        options.region.center,
        options.region.span
      );
    } else if (options?.center && options?.zoom) {
      mapOptions.region = this.createCoordinateRegion(
        { latitude: options.center.lat, longitude: options.center.lng },
        this.zoomToSpan(options.zoom)
      );
    }

    if (options?.mapType) {
      mapOptions.mapType = options.mapType;
    }

    return mapOptions;
  }

  private zoomToSpan(zoom: number): MapKitCoordinateSpan {
    const delta = Math.max(0.002, 0.5 / Math.pow(2, zoom - 10));
    return { latitudeDelta: delta, longitudeDelta: delta };
  }

  private resolveIconPath(path: string | any): string {
    if (!path) {
      return path;
    }

    // Si c'est un objet (Leaflet icon config), extraire l'URL
    if (typeof path === 'object') {
      path = path.iconUrl || path.url || path.icon || '';
    }

    // S'assurer que c'est une chaîne
    if (typeof path !== 'string') {
      return '';
    }

    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("data:")
    ) {
      return path;
    }
    if (path.startsWith("/")) {
      return `${window.location.origin}${path}`;
    }
    return `${window.location.origin}/${path}`;
  }

  private async ensureInitialized(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      await this.loadMapKitScript();

      await new Promise<void>((resolve, reject) => {
        mapkit.init({
          authorizationCallback: async (done: (token: string) => void) => {
            try {
              const token = await this.fetchAuthorizationToken();
              done(token);

              if (!this.isInitialized) {
                this.geocoder = new mapkit.Geocoder();
                this.directions = new mapkit.Directions();
                this.isInitialized = true;
                resolve();
              }
            } catch (error) {
              reject(error);
            }
          },
        });
      });
    })()
      .catch((error) => {
        this.isInitialized = false;
        throw error;
      })
      .finally(() => {
        this.initPromise = undefined;
      });

    return this.initPromise;
  }

  private async loadMapKitScript(): Promise<void> {
    if (typeof mapkit !== 'undefined') {
      return;
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src =
        environment.mapkit?.scriptUrl ||
        'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
      script.async = true;
      script.onload = () => {

        resolve();
      };
      script.onerror = (error) => {
        // Restore original console.warn on error

        reject(new Error('Unable to load MapKit JS script'));
      };
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  private async fetchAuthorizationToken(): Promise<string> {
    const endpoint =
      environment.mapkit?.tokenEndpoint ||
      `${environment.apiUrl}/clients/mapkey/generate`;

    return await lastValueFrom(
      this.http.get(endpoint, { responseType: 'text' })
    );
  }

  getMrakerIcon(type: string, time: any = ""): {

    icon: string;
    width: number;
    height: number;
  } {
    const iconParticulierUrl = `/assets/icon/house.svg`;
    const iconPrestataireUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(getSvgMarker(time));
    const RedLocationPin = `/assets/icon/map-icons/location-pin-red.png`;
    const BlackLocationPin = `/assets/icon/map-icons/location-pin-black.png`;
    const BlueLocationPin = `/assets/icon/map-icons/location-pin-blue.png`;
    const iconepickup = `/assets/icon/map-icons/icone_pickup.svg`;
    const iconeDestination = `/assets/icon/map-icons/icone_destination.svg`;
    const homeIconWidth = 35;
    const homeIconHeight = 66;
    const carIconWidth = 42;
    const carIconHeight = 81;
    let width: number = 0;
    let height: number = 0;
    let icon = "";

    switch (type) {
      case "particulier":
        icon = iconParticulierUrl;
        width = homeIconWidth;
        height = homeIconHeight;
        break;
      case "prestataire":
        icon = iconPrestataireUrl;
        width = carIconWidth;
        height = carIconHeight;
        break;
      case "point_of_arrival":
        icon = iconeDestination;
        width = homeIconWidth + 15;
        height = homeIconHeight + 15;
        break;
      case "starting_point":
        icon = iconepickup;
        width = homeIconWidth + 15;
        height = homeIconHeight + 15;
        break;
      default:
        icon = BlackLocationPin;
        width = homeIconWidth;
        height = homeIconHeight;
        break;
    }

    return {
      icon: icon,
      width: width,
      height: height,
    };
  }
}
